/**
 * DL-01 — the "Download images" dialog: ONE flow for a data set, an
 * instruction or a project (story §B).
 *
 *   unsupported   no `showDirectoryPicker` (Firefox / Safari): "Use Chrome
 *                 or Edge, or the CLI" — before anything is asked;
 *   loading       the manifest(s) requested (lyse/v1, `manifestApi.ts`);
 *   refused       413 too large → the split hint; 403 → the server's words;
 *   ready         files · bytes · data sets, the skipped count; above 10 GB
 *                 the warning with the required bytes and the free-space
 *                 hint (the browser cannot read a drive's free space);
 *                 "Choose folder and download" opens the picker IN the click
 *                 (a user gesture) and hands the run to the download manager;
 *   item          DL-02 — the dialog FOLLOWS its item in the manager
 *                 (`DownloadManagerProvider`): queued / running (verify, then
 *                 progress per data set) / stopped (disk full, a write
 *                 failed, Stop — Resume re-verifies into the SAME folder) /
 *                 done (files that failed offer Resume). Minimise, a click
 *                 beside the dialog or Escape close the dialog only — the run
 *                 goes on, the shell's status bar shows it (Christian
 *                 2026-10-22: closing must never stop a download).
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useApi } from "@aeriosiq/api-client";
import { DENSITY_ATTR } from "@aeriosiq/ui";
import { useDownloadManager } from "./DownloadManagerProvider.js";
import { classifyManifestError, type DownloadRefusal } from "./errors.js";
import { pickDownloadRoot, supportsFolderDownload } from "./fs.js";
import { fileWord, sizeGuardOf, type SizeGuard } from "./guards.js";
import type { DownloadItem, DownloadItemState } from "./manager.js";
import {
  requestManifests,
  type DownloadScope,
  type ManifestBundle,
  type ManifestClient,
} from "./manifestApi.js";
import type { RunProgress } from "./run.js";
import { WORDS } from "./words.js";

type Flow =
  | { step: "unsupported" }
  | { step: "loading" }
  | { step: "refused"; refusal: DownloadRefusal }
  | { step: "ready"; bundle: ManifestBundle; guard: SizeGuard }
  | { step: "item"; itemId: string; bundle: ManifestBundle };

export interface DownloadImagesDialogProps {
  /** The scope to download; `null` = closed. */
  scope: DownloadScope | null;
  onClose: () => void;
  /** The manifest reader (defaults to the host client); tests pass a stub. */
  client?: ManifestClient | undefined;
  /** The feature probe (defaults to the real one); tests force a browser. */
  supported?: boolean | undefined;
}

export function DownloadImagesDialog(
  props: DownloadImagesDialogProps,
): ReactElement {
  const { scope, onClose } = props;
  return (
    <Dialog
      open={scope !== null}
      onClose={onClose}
      aria-labelledby="dl-dialog-title"
      data-testid="dl-dialog"
      maxWidth="sm"
      fullWidth
      // a dialog is portaled outside the shell's compact <main>: the paper
      // declares the compact surface itself (D77 — once, on the region)
      PaperProps={{ [DENSITY_ATTR]: "compact" }}
    >
      {scope && <DownloadFlow {...props} scope={scope} />}
    </Dialog>
  );
}

function stoppedWords(p: RunProgress): string {
  const stop = p.stop;
  if (!stop) return WORDS.stoppedByUser(p.filesDone, p.filesTotal);
  switch (stop.kind) {
    case "disk_full":
      return WORDS.diskFull(p.filesDone, p.filesTotal, p.bytesWritten);
    case "write_failed":
      return WORDS.writeFailed(
        stop.fileName,
        p.filesDone,
        p.filesTotal,
        stop.detail,
      );
    case "stopped":
      return WORDS.stoppedByUser(p.filesDone, p.filesTotal);
  }
}

/** The body — mounted only while open, so the manifest is read only then. */
function DownloadFlow({
  scope,
  onClose,
  client,
  supported,
}: DownloadImagesDialogProps & { scope: DownloadScope }): ReactElement {
  const hostApi = useApi();
  const api: ManifestClient = client ?? hostApi;
  const manager = useDownloadManager();
  const [flow, setFlow] = useState<Flow>(() =>
    (supported ?? supportsFolderDownload())
      ? { step: "loading" }
      : { step: "unsupported" },
  );

  // the manifest(s), ONCE per open: the in-flight request is held in a ref,
  // so StrictMode's mount → cleanup → mount of this effect shares one
  // request instead of sending two (the second would also hide an expired
  // first manifest in the journey)
  const manifestReq = useRef<Promise<ManifestBundle> | null>(null);
  useEffect(() => {
    if (flow.step !== "loading") return;
    let cancelled = false;
    manifestReq.current ??= requestManifests(api, scope);
    manifestReq.current
      .then((bundle) => {
        if (cancelled) return;
        setFlow({
          step: "ready",
          bundle,
          guard: sizeGuardOf(bundle.totalBytes, bundle.warnings),
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setFlow({ step: "refused", refusal: classifyManifestError(e) });
      });
    return () => {
      cancelled = true;
    };
    // the read happens once per open: `flow.step` only flips to "loading" at mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.step === "loading", scope.kind, scope.id]);

  const start = async (bundle: ManifestBundle): Promise<void> => {
    // the picker must be opened inside the click (a user gesture)
    const root = await pickDownloadRoot();
    if (!root) return;
    const itemId = manager.enqueue(scope, bundle, root);
    setFlow({ step: "item", itemId, bundle });
  };

  const item: DownloadItem | undefined =
    flow.step === "item" ? manager.itemOf(flow.itemId) : undefined;
  const itemState: DownloadItemState | null = item?.state ?? null;
  const busy =
    itemState === "running" || itemState === "queued" || itemState === "paused";
  const queuePosition =
    item?.state === "queued"
      ? manager.items.filter(
          (it) =>
            (it.state === "running" || it.state === "queued") &&
            it.enqueuedAt <= item.enqueuedAt &&
            it.id !== item.id,
        ).length + 1
      : 0;

  return (
    <>
      <DialogTitle id="dl-dialog-title">
        {WORDS.title(scope)}
        {scope.label ? (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ display: "block" }}
            data-testid="dl-scope-label"
          >
            {scope.label}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {flow.step === "unsupported" && (
            <Alert severity="warning" data-testid="dl-unsupported">
              {WORDS.unsupported}
            </Alert>
          )}
          {flow.step === "loading" && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} aria-label={WORDS.loading} />
              <Typography variant="body2">{WORDS.loading}</Typography>
            </Stack>
          )}
          {flow.step === "refused" && (
            <Alert
              severity={flow.refusal.kind === "too_large" ? "warning" : "error"}
              data-testid="dl-refusal"
              data-kind={flow.refusal.kind}
            >
              {WORDS.refusal(flow.refusal)}
            </Alert>
          )}
          {flow.step === "ready" && (
            <ReadyBody bundle={flow.bundle} guard={flow.guard} />
          )}
          {flow.step === "item" && item && (
            <ProgressBody
              state={item.state}
              progress={item.progress}
              rootName={item.root.name}
              sets={item.bundle.manifests.length}
              queuePosition={queuePosition}
            />
          )}
          {flow.step === "item" && !item && (
            <Alert severity="info" data-testid="dl-removed">
              {WORDS.removedFromList}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {busy && item ? (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                manager.stop(item.id);
              }}
              data-testid="dl-stop"
            >
              {WORDS.stop}
            </Button>
            <Button
              variant="text"
              onClick={onClose}
              data-testid="dl-minimise"
              title={WORDS.minimiseHint}
            >
              {WORDS.minimise}
            </Button>
          </>
        ) : (
          <Button variant="text" onClick={onClose} data-testid="dl-close">
            {flow.step === "ready" ? WORDS.cancel : WORDS.close}
          </Button>
        )}
        {flow.step === "ready" && flow.bundle.imageCount > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              void start(flow.bundle);
            }}
            data-testid="dl-start"
          >
            {WORDS.chooseFolder}
          </Button>
        )}
        {item &&
          (item.state === "stopped" ||
            item.state === "paused" ||
            (item.state === "done" &&
              (item.progress?.failures.length ?? 0) > 0)) && (
            <Button
              variant="contained"
              onClick={() => {
                manager.resume(item.id);
              }}
              data-testid="dl-resume"
            >
              {WORDS.resume}
            </Button>
          )}
      </DialogActions>
    </>
  );
}

function ReadyBody({
  bundle,
  guard,
}: {
  bundle: ManifestBundle;
  guard: SizeGuard;
}): ReactElement {
  return (
    <>
      <Typography variant="body1" data-testid="dl-summary">
        {WORDS.summary(
          bundle.imageCount,
          bundle.totalBytes,
          bundle.manifests.length,
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {WORDS.fullResolution}
      </Typography>
      {bundle.skippedCount > 0 && (
        <Typography variant="body2" data-testid="dl-skipped">
          {WORDS.skipped(bundle.skippedCount)}
        </Typography>
      )}
      {bundle.imageCount === 0 ? (
        <Alert severity="info" data-testid="dl-empty">
          Nothing to download — no image of this scope is available.
        </Alert>
      ) : guard === "warn" ? (
        <Alert severity="warning" data-testid="dl-large-warning">
          <AlertTitle>{WORDS.largeWarningTitle}</AlertTitle>
          {WORDS.largeWarning(bundle.totalBytes)}
        </Alert>
      ) : (
        <Typography variant="body2" data-testid="dl-required">
          {WORDS.requiredBefore(bundle.totalBytes)}
        </Typography>
      )}
    </>
  );
}

function ProgressBody({
  state,
  progress,
  rootName,
  sets,
  queuePosition,
}: {
  state: DownloadItemState;
  progress: RunProgress | null;
  rootName: string;
  sets: number;
  queuePosition: number;
}): ReactElement {
  const p = progress;
  const pct =
    p && p.filesTotal > 0
      ? Math.min(100, Math.round((p.filesDone / p.filesTotal) * 100))
      : 0;
  const line =
    state === "queued"
      ? WORDS.queued(queuePosition)
      : state === "paused"
        ? WORDS.pausedHint
        : state === "running"
          ? !p?.verified
            ? WORDS.verifying
            : WORDS.running(p.filesDone, p.filesTotal)
          : state === "stopped"
            ? p
              ? stoppedWords(p)
              : WORDS.stoppedByUser(0, 0)
            : p
              ? WORDS.done(p.filesDone, p.filesPresent, p.bytesWritten)
              : WORDS.done(0, 0, 0);
  return (
    <>
      <LinearProgress
        variant={p && state !== "queued" ? "determinate" : "indeterminate"}
        value={pct}
        aria-label={WORDS.progressLabel}
        data-testid="dl-progress-bar"
      />
      {state === "stopped" ? (
        <Alert
          severity="warning"
          data-testid="dl-stopped"
          data-kind={p?.stop?.kind ?? "stopped"}
        >
          {line}
        </Alert>
      ) : state === "done" ? (
        <Alert severity="success" data-testid="dl-done">
          {line}
        </Alert>
      ) : (
        <Typography variant="body2" data-testid="dl-progress-line">
          {line}
        </Typography>
      )}
      {(state === "running" || state === "queued" || state === "paused") && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="dl-minimise-hint"
        >
          {WORDS.minimiseHint}
        </Typography>
      )}
      {p && p.verified && state === "running" && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="dl-required-after"
        >
          {WORDS.requiredAfterVerify(p.requiredBytes, rootName)}
        </Typography>
      )}
      {p && p.filesPresent > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="dl-present"
        >
          {WORDS.present(
            p.das.reduce((n, d) => n + d.presentMd5, 0),
            p.das.reduce((n, d) => n + d.presentSize, 0),
          )}
        </Typography>
      )}
      {p && p.linksRenewed && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="dl-renewed"
        >
          {WORDS.expiredRetried}
        </Typography>
      )}
      {state === "done" && p && p.failures.length > 0 && (
        <Alert severity="warning" data-testid="dl-failures">
          {WORDS.doneWithFailures(p.failures.length)}
        </Alert>
      )}
      {p && sets > 1 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="dl-da-list">
          {p.das.map((d) => (
            <Typography
              key={d.daId}
              component="li"
              variant="body2"
              color="text.secondary"
            >
              {d.folderName} · {fileWord(d.done)} / {String(d.total)}
              {d.failed > 0 ? ` · ${fileWord(d.failed)} failed` : ""}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
}
